import { RotateCcw } from 'lucide-react';
import { Button, Checkbox } from '@/components/ui';
import { getSelectOptions, getTitleEnumValue } from '@/functions';
import { useQueryParams } from '@/hooks';
import { useCallback } from 'react';
import { AccountingUserTypeEnum } from '@/enums/accountingUserType.enum';
import { AccountingUserStatusEnum } from '@/enums/accountingUserStatus.enum';

const AccountingFiltersSidebar = () => {
    const { updateSearchParams, urlSearchParams } = useQueryParams(['type', 'status']);

    const handleFilterChange = useCallback((key: string, value: string, checked: boolean) => {
        updateSearchParams(l => {
            if (checked) {
                l.append(key, value);
            } else {
                l.delete(key, value);
            }
            l.set('page', '1');
            return l;
        });
    }, [updateSearchParams]);

    const handleReset = () => {
        updateSearchParams(l => {
            l.delete('type');
            l.delete('status');
            l.set('page', '1');
            l.append('type', AccountingUserTypeEnum.INVESTOR);
            l.append('type', AccountingUserTypeEnum.EMPLOYEE);
            l.append('status', AccountingUserStatusEnum.ACTIVE);
            return l;
        });
    };

    const typeOptions = getSelectOptions('AccountingUserTypeEnum').filter(o => o.value);
    const statusOptions = getSelectOptions('AccountingUserStatusEnum').filter(o => o.value);

    const activeTypes = urlSearchParams.getAll('type');
    const activeStatuses = urlSearchParams.getAll('status');

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-end">
                <Button variant="ghost" size="sm" onClick={handleReset} className="h-8 text-xs text-muted-foreground hover:text-foreground">
                    <RotateCcw size={14} className="mr-1" />
                    Reset
                </Button>
            </div>

            {/* Filter by User Type */}
            <div className="space-y-3">
                <h4 className="text-sm font-medium">Type d'utilisateur</h4>
                <div className="space-y-2.5">
                    {typeOptions.map(option => (
                        <div key={option.value} className="flex items-center space-x-2.5">
                            <Checkbox
                                id={`type-${option.value}`}
                                checked={activeTypes.includes(option.value as string)}
                                onCheckedChange={(checked) =>
                                    handleFilterChange('type', option.value as string, checked as boolean)
                                }
                            />
                            <label htmlFor={`type-${option.value}`} className="text-sm leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                {getTitleEnumValue('AccountingUserTypeEnum', option.value as string)}
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Filter by Status */}
            <div className="space-y-3">
                <h4 className="text-sm font-medium">Statut</h4>
                <div className="space-y-2.5">
                    {statusOptions.map(option => (
                        <div key={option.value} className="flex items-center space-x-2.5">
                            <Checkbox
                                id={`status-${option.value}`}
                                checked={activeStatuses.includes(option.value as string)}
                                onCheckedChange={(checked) =>
                                    handleFilterChange('status', option.value as string, checked as boolean)
                                }
                            />
                            <label htmlFor={`status-${option.value}`} className="text-sm leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                {getTitleEnumValue('AccountingUserStatusEnum', option.value as string)}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AccountingFiltersSidebar;
